import { create } from "zustand";
import { count as outboxCount, list as outboxList, remove, updateAttempt } from "../lib/offlineOutbox";
import { api } from "../lib/api";

/**
 * Tracks online status and pending offline mutations. Handles automatic
 * replay when the tab comes back online.
 *
 * State:
 *   online         — navigator.onLine mirror (kept in sync via events)
 *   pendingCount   — number of items sitting in the IndexedDB outbox
 *   syncing        — replay loop is currently running
 *   lastSyncedAt   — ISO string of the most recent successful drain
 *   lastError      — human-readable message from the last replay attempt
 *
 * On success we emit a `serpix:outbox-drained` window event so tasks store
 * can refetch and reconcile client-side temp IDs with server IDs.
 */

export const useOnlineStore = create((set, get) => ({
  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  pendingCount: 0,
  syncing: false,
  lastSyncedAt: null,
  lastError: null,

  async refreshCount() {
    try {
      const c = await outboxCount();
      set({ pendingCount: c });
    } catch {
      /* IndexedDB unavailable — private mode, etc. */
    }
  },

  setOnline(online) {
    const wasOnline = get().online;
    set({ online });
    if (!wasOnline && online) {
      // Just came back — try to drain.
      get().drain();
    }
  },

  async drain() {
    if (get().syncing) return;
    if (!get().online) return;
    set({ syncing: true, lastError: null });
    try {
      const rows = (await outboxList()).sort((a, b) => a.id - b.id);
      let processed = 0;
      for (const row of rows) {
        try {
          const cfg = {
            method: row.method || "POST",
            url: row.url,
            data: row.body,
            headers: row.token
              ? { Authorization: `Bearer ${row.token}` }
              : undefined,
          };
          const res = await api.request(cfg);
          await remove(row.id);
          processed += 1;
          // Fan out so stores can reconcile (e.g. replace temp task id).
          window.dispatchEvent(
            new CustomEvent("serpix:outbox-replayed", {
              detail: { entry: row, response: res.data },
            })
          );
        } catch (err) {
          const status = err?.response?.status;
          if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
            // Permanent client error — drop it so we don't retry forever.
            await remove(row.id);
            window.dispatchEvent(
              new CustomEvent("serpix:outbox-dropped", {
                detail: { entry: row, error: err?.response?.data || err.message },
              })
            );
          } else {
            await updateAttempt(row.id, err?.message);
            throw err; // stop the loop, we'll retry later
          }
        }
      }
      set({ lastSyncedAt: new Date().toISOString() });
      if (processed > 0) {
        window.dispatchEvent(
          new CustomEvent("serpix:outbox-drained", { detail: { processed } })
        );
      }
    } catch (err) {
      set({ lastError: err?.message || String(err) });
    } finally {
      await get().refreshCount();
      set({ syncing: false });
    }
  },
}));

/**
 * Initialise browser event listeners exactly once. Call from App.js.
 */
export function initOnlineTracker() {
  const store = useOnlineStore.getState();
  store.refreshCount();

  const onOnline = () => useOnlineStore.getState().setOnline(true);
  const onOffline = () => useOnlineStore.getState().setOnline(false);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  // Periodically double-check — some browsers keep `navigator.onLine` true
  // even after wifi drops until an actual request fails. A cheap /api ping
  // every 30 s catches those cases.
  const ping = async () => {
    try {
      await fetch(
        (process.env.REACT_APP_BACKEND_URL || "") + "/api/",
        { method: "GET", cache: "no-store" }
      );
      if (!useOnlineStore.getState().online) {
        useOnlineStore.getState().setOnline(true);
      }
    } catch {
      if (useOnlineStore.getState().online) {
        useOnlineStore.getState().setOnline(false);
      }
    }
  };
  const timer = setInterval(ping, 30_000);

  // First drain attempt shortly after boot (in case we start with a pending queue).
  setTimeout(() => useOnlineStore.getState().drain(), 800);

  return () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    clearInterval(timer);
  };
}
